import React from 'react';
import { startCase } from 'lodash';

import { ActionFooter } from '~/components/AiChat/SideBarDialog/Actions/Action/ActionFooter';
import { AssessDamageActionInner } from '~/components/AiChat/SideBarDialog/Actions/Action/AssessDamage/AssessDamageActionInner';
import { CommunicateActionInner } from '~/components/AiChat/SideBarDialog/Actions/Action/Communicate/CommunicateActionInner';
import { DemoActionsFooter } from '~/components/AiChat/SideBarDialog/Actions/Action/DemoActionFooter';
import { GeneralActionInner } from '~/components/AiChat/SideBarDialog/Actions/Action/GeneralActionInner';
import { InvoiceAnalysisActionInner } from '~/components/AiChat/SideBarDialog/Actions/Action/InvoiceAnalysis/InvoiceAnalysisActionInner';
import { InvoiceAnalysisFooter } from '~/components/AiChat/SideBarDialog/Actions/Action/InvoiceAnalysis/InvoiceAnalysisFooter';
import { ReviewAndExecuteFooter } from '~/components/AiChat/SideBarDialog/Actions/Action/ReviewAndExecute/ReviewAndExecuteFooter';
import type { AiAction, AiActionType, DynamicClaimActionTaskData } from '~/components/AiChat/types';
import { AI_ACTION_TYPES } from '~/components/AiChat/types';

export interface ActionsPanelProps {
  action: AiAction;
}

export interface ActionFooterProps {
  onExecute?: () => void;
  onUnexecute?: () => void;
  onDismiss?: () => void;
  isDone: boolean;
  isDemo?: boolean;
  action: AiAction;
  type: AiActionType;
  executeLabel?: string;
  /** The turn-down button's label; "Dismiss" unless the surface calls it something else. */
  dismissLabel?: string;
  /** The action's name, for a footer that opens a dialog about it. */
  title?: string;
  /** The exposure the action is about, where there is one. */
  subtitle?: string;
}

interface InnerTypeDescriptor {
  title: string | ((action: AiAction) => string);
  InnerActionComponent: React.ElementType<ActionsPanelProps>;
  ActionFooterComponent?: React.ElementType<ActionFooterProps>;
  // Marks a duplicated type that has a backend registry tool. When this is set AND the
  // claim_actions_configured_execution feature flag is on, the hook routes execution to
  // the generic /execute_action_tool endpoint and swaps in the review-and-execute footer;
  // otherwise the legacy /execute path + this descriptor's footer are used.
  usesConfiguredExecution?: boolean;
}

const getInvoiceActionTitle = (action: AiAction): string => {
  const taskData = action.task_data as { document_claim_internal_id?: number };
  const documentIndex = taskData.document_claim_internal_id;

  return `Invoice ${documentIndex ? `#${documentIndex} ` : ''}Analysis`;
};

const getDynamicActionTitle = (action: AiAction): string => {
  const taskData = action.task_data as DynamicClaimActionTaskData;
  const taskText = taskData?.task_text;
  const toolKey = taskData?.tool_key;

  // The conductor persists the tool key itself as `task_text` (`create_inspection`), which is an
  // identifier, not a title. The key's words are the title, in the Title Case CMS names actions in.
  if (toolKey && (!taskText || taskText === toolKey)) return startCase(toolKey);

  return taskText || 'Claim Action';
};

const DynamicClaimActionInner: React.FC<ActionsPanelProps> = ({ action }) => (
  <GeneralActionInner action={action} displayTitle={false} />
);

export const ACTION_TYPE_DESCRIPTOR: Record<string, InnerTypeDescriptor> = {
  [AI_ACTION_TYPES.INVOICE_ANALYSIS_RECOMMENDATION]: {
    title: getInvoiceActionTitle,
    InnerActionComponent: InvoiceAnalysisActionInner,
    ActionFooterComponent: InvoiceAnalysisFooter,
    usesConfiguredExecution: true,
  },
  [AI_ACTION_TYPES.COMMUNICATE]: {
    title: 'Communicate',
    InnerActionComponent: CommunicateActionInner,
    ActionFooterComponent: DemoActionsFooter,
  },
  [AI_ACTION_TYPES.ADJUSTER_ASSIGNMENT]: {
    title: 'Adjuster Assignment',
    InnerActionComponent: GeneralActionInner,
    ActionFooterComponent: DemoActionsFooter,
  },
  [AI_ACTION_TYPES.OPEN_EXPOSURE]: {
    title: 'Open Exposure',
    InnerActionComponent: GeneralActionInner,
    ActionFooterComponent: DemoActionsFooter,
  },
  [AI_ACTION_TYPES.SET_RESERVE]: {
    title: 'Set Reserve',
    InnerActionComponent: GeneralActionInner,
    ActionFooterComponent: DemoActionsFooter,
  },
  [AI_ACTION_TYPES.ASSESS_DAMAGE]: {
    title: 'Assess Damage',
    InnerActionComponent: AssessDamageActionInner,
    ActionFooterComponent: ActionFooter,
  },
  // Generic renderer for the unified dynamic claim action: title from `task_text`, the generic
  // inner component, and the review-and-execute footer (execution is routed to the configured
  // tool path by useActionCard).
  [AI_ACTION_TYPES.DYNAMIC_CLAIM_ACTION]: {
    title: getDynamicActionTitle,
    InnerActionComponent: DynamicClaimActionInner,
    ActionFooterComponent: ReviewAndExecuteFooter,
  },
};
